import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Button, IconButton, Chip, Typography, Grid,
  CircularProgress, Collapse, Box, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, ClickAwayListener, Tooltip, Checkbox,
  FormControlLabel
} from '@material-ui/core'
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Add as AddIcon, Search as SearchIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Map as MapIcon, ViewList as ListIcon,
  LocationOn as LocationOnIcon, Business as BusinessIcon,
  HomeWork as HomeWorkIcon,
  Warning as WarningIcon
} from '@material-ui/icons'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const DEFAULT_CENTER = [33.55, -112.0]

const FACILITY_TYPE_LABELS = {
  county_transfer_station: 'County Transfer',
  regional_landfill: 'Regional Landfill',
  city_landfill: 'City Landfill',
  inert_landfill: 'Inert Landfill',
  inert_landfill_specialty: 'Inert Specialty',
  private_landfill: 'Private Landfill',
  private_transfer_station: 'Private Transfer',
  city_transfer_station: 'City Transfer',
  city_collection_center: 'City Collection',
  tribal_landfill: 'Tribal Landfill'
}

const MATERIAL_CATEGORIES = [
  'concrete', 'dirt', 'rock', 'asphalt', 'tile',
  'green_waste', 'c_and_d', 'msw',
  'appliances', 'electronics', 'tires', 'scrap_metal',
  'special_waste', 'sludge', 'contaminated_soil'
]

const MATERIAL_COLORS = {
  concrete: { bg: '#eef0f1', color: '#546e7a' },
  dirt: { bg: '#f0ebe6', color: '#6d4c41' },
  rock: { bg: '#eceff1', color: '#607d8b' },
  asphalt: { bg: '#e8ecef', color: '#455a64' },
  tile: { bg: '#f0f2f3', color: '#78909c' },
  green_waste: { bg: '#dceedf', color: '#2f7a3e' },
  c_and_d: { bg: '#fbe8d6', color: '#b35d1a' },
  msw: { bg: '#f7dccf', color: '#a9341d' },
  appliances: { bg: '#ece2fa', color: '#6e3fb7' },
  electronics: { bg: '#e6ebfa', color: '#3454c1' },
  tires: { bg: '#e8e8e8', color: '#424242' },
  scrap_metal: { bg: '#ececec', color: '#757575' },
  special_waste: { bg: '#fce4ec', color: '#c62828' },
  sludge: { bg: '#efebe9', color: '#5d4037' },
  contaminated_soil: { bg: '#fbe9e7', color: '#bf360c' }
}
// Helper to get chip style from the new color format
const matChipStyle = (cat, opts = {}) => {
  const mc = MATERIAL_COLORS[cat] || { bg: '#f0f0f0', color: '#666' }
  return {
    backgroundColor: mc.bg, color: mc.color,
    fontWeight: 600, border: `1px solid ${mc.color}22`,
    ...opts
  }
}

const MATERIAL_LABELS = {
  concrete: 'Concrete', dirt: 'Dirt', rock: 'Rock', asphalt: 'Asphalt',
  tile: 'Tile', green_waste: 'Green Waste', c_and_d: 'C&D', msw: 'MSW',
  appliances: 'Appliances', electronics: 'Electronics', tires: 'Tires',
  scrap_metal: 'Scrap Metal', special_waste: 'Special Waste',
  sludge: 'Sludge', contaminated_soil: 'Contaminated Soil'
}

const FACILITY_TYPE_FILTER_GROUPS = [
  { label: 'All', value: 'All' },
  { label: 'County', value: 'county_transfer_station' },
  { label: 'Regional Landfill', value: 'regional_landfill' },
  { label: 'City Landfill', value: 'city_landfill' },
  { label: 'Inert Landfill', value: 'inert_landfill' },
  { label: 'Private', value: 'private' },
  { label: 'City Transfer', value: 'city_transfer_station' },
  { label: 'Tribal', value: 'tribal_landfill' },
  { label: 'Collection Center', value: 'city_collection_center' }
]

const FACILITY_MARKER_COLORS = {
  regional_landfill: '#e53935', county_transfer_station: '#1e88e5',
  inert_landfill: '#78909c', inert_landfill_specialty: '#78909c',
  city_landfill: '#43a047', city_transfer_station: '#43a047',
  city_collection_center: '#43a047', private_landfill: '#fb8c00',
  private_transfer_station: '#fb8c00', tribal_landfill: '#8e24aa'
}

const DUMP_FEE_UNIT_LABELS = { PER_TON: 'Per Ton', PER_LOAD: 'Per Load', FLAT: 'Flat' }
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const HMI_YARD = { lat: 33.43730763333812, lng: -112.11879940396678 }

function haversineDistMiles (lat1, lng1, lat2, lng2) {
  const R = 3958.8
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function yardDistLabel (item) {
  if (!item.location?.coordinates?.length) return null
  const [lon, lat] = item.location.coordinates
  if (!lon || !lat) return null
  const miles = haversineDistMiles(HMI_YARD.lat, HMI_YARD.lng, lat, lon)
  return `${Math.round(miles * 10) / 10} mi`
}

function buildMarkerIcon (facilityType, highlight) {
  const color = highlight ? '#ff1744' : (FACILITY_MARKER_COLORS[facilityType] || '#78909c')
  const size = highlight ? 28 : 22
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  })
}

// Global callback ref for popup edit buttons (Leaflet popups are HTML strings, not React)
window.__disposalEditFacility = null

function parseHoursToStructured (hoursStr) {
  const result = {}
  DAYS_OF_WEEK.forEach(d => { result[d] = { open: false, openTime: '07:00', closeTime: '16:00' } })
  if (!hoursStr) return result
  const lower = hoursStr.toLowerCase()
  if (lower.includes('mon-fri')) ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].forEach(d => { result[d].open = true })
  if (lower.includes('mon-sat')) ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(d => { result[d].open = true })
  if (lower.includes('wed-sat')) ['Wed', 'Thu', 'Fri', 'Sat'].forEach(d => { result[d].open = true })
  if (lower.includes('thu-fri')) ['Thu', 'Fri'].forEach(d => { result[d].open = true })
  if (lower.includes('fri and sat') || lower.includes('fri-sat')) ['Fri', 'Sat'].forEach(d => { result[d].open = true })
  if (lower.includes('wed and sat')) ['Wed', 'Sat'].forEach(d => { result[d].open = true })
  if (lower.includes('thu-mon')) ['Thu', 'Fri', 'Sat', 'Sun', 'Mon'].forEach(d => { result[d].open = true })
  DAYS_OF_WEEK.forEach(day => {
    if (lower.includes(day.toLowerCase()) && !result[day].open) result[day].open = true
  })
  if (lower.includes('closed sun')) result.Sun.open = false
  const timeMatch = hoursStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)/g)
  if (timeMatch && timeMatch.length >= 2) {
    const parseTime = (t) => {
      const m = t.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)/i)
      if (!m) return null
      let h = parseInt(m[1])
      const min = m[2] || '00'
      if (m[3].toUpperCase() === 'PM' && h < 12) h += 12
      if (m[3].toUpperCase() === 'AM' && h === 12) h = 0
      return `${String(h).padStart(2, '0')}:${min}`
    }
    const openT = parseTime(timeMatch[0])
    const closeT = parseTime(timeMatch[1])
    if (openT && closeT) DAYS_OF_WEEK.forEach(d => { if (result[d].open) { result[d].openTime = openT; result[d].closeTime = closeT } })
  }
  return result
}

function structuredToHoursString (structured) {
  const openDays = DAYS_OF_WEEK.filter(d => structured[d]?.open)
  if (openDays.length === 0) return 'Closed'
  const groups = []
  let current = null
  for (const day of openDays) {
    const h = structured[day]
    if (current && current.openTime === h.openTime && current.closeTime === h.closeTime) {
      current.days.push(day)
    } else {
      current = { days: [day], openTime: h.openTime, closeTime: h.closeTime }
      groups.push(current)
    }
  }
  const fmt = (t) => {
    const [h, m] = t.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return m === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }
  return groups.map(g => {
    const dayRange = g.days.length === 1 ? g.days[0] : `${g.days[0]}-${g.days[g.days.length - 1]}`
    return `${dayRange} ${fmt(g.openTime)} - ${fmt(g.closeTime)}`
  }).join(', ')
}

// ── Section header used inside modal ────────────────────────────

const SectionHeader = ({ children, style }) => (
  <div style={{
    fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
    color: 'var(--ink-3, #6f7a6b)', marginBottom: 10, paddingBottom: 6,
    borderBottom: '2px solid var(--line, #e0d8c2)',
    fontFamily: 'var(--font-mono)', ...style
  }}>
    {children}
  </div>
)

// ── Edit/Create Modal ──────────────────────────────────────────────

const FacilityFormModal = ({ open, onClose, onSave, editItem, url }) => {
  const isEdit = !!editItem
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [structuredHours, setStructuredHours] = useState({})
  const [relocatingPin, setRelocatingPin] = useState(false)

  const [nameResults, setNameResults] = useState([])
  const [showNameResults, setShowNameResults] = useState(false)
  const nameTimer = useRef(null)
  const [addrSuggestions, setAddrSuggestions] = useState([])
  const [showAddrSuggestions, setShowAddrSuggestions] = useState(false)
  const [addrResolved, setAddrResolved] = useState(false)
  const addrTimer = useRef(null)

  const miniMapRef = useRef(null)
  const miniMapContainerRef = useRef(null)
  const pinMarkerRef = useRef(null)

  useEffect(() => {
    if (!open) return
    if (editItem) {
      const coords = editItem.location?.coordinates
      setForm({
        name: editItem.name || '', address: editItem.address || '',
        owner: editItem.owner || '', facilityType: editItem.facilityType || 'regional_landfill',
        phone: editItem.phone || '', payment: editItem.payment || '',
        notes: editItem.notes || '', defaultDumpFee: editItem.defaultDumpFee || '',
        dumpFeeUnit: editItem.dumpFeeUnit || 'PER_LOAD',
        doesNotAccept: (editItem.doesNotAccept || []).join(', '),
        acceptedMaterials: (editItem.acceptedMaterials || []).map(m => m.category),
        materialDetails: (editItem.acceptedMaterials || []).reduce((acc, m) => {
          acc[m.category] = { details: m.details || '', costPerTon: m.costPerTon || '', costPerLoad: m.costPerLoad || '' }
          return acc
        }, {}),
        latitude: coords?.[1] != null ? String(coords[1]) : '',
        longitude: coords?.[0] != null ? String(coords[0]) : ''
      })
      setStructuredHours(parseHoursToStructured(editItem.hours))
      setAddrResolved(!!(coords?.[0] && coords?.[1]))
      setRelocatingPin(false)
    } else {
      setForm({
        name: '', address: '', owner: '', facilityType: 'regional_landfill',
        phone: '', payment: '', notes: '', defaultDumpFee: '', dumpFeeUnit: 'PER_LOAD',
        doesNotAccept: '', acceptedMaterials: [], materialDetails: {},
        latitude: '', longitude: ''
      })
      setStructuredHours(parseHoursToStructured(''))
      setAddrResolved(false)
      setRelocatingPin(true)
    }
    setSaving(false); setNameResults([]); setShowNameResults(false)
    setAddrSuggestions([]); setShowAddrSuggestions(false)
    return () => {
      if (nameTimer.current) clearTimeout(nameTimer.current)
      if (addrTimer.current) clearTimeout(addrTimer.current)
    }
  }, [open, editItem])

  // Mini map
  useEffect(() => {
    if (!open) {
      if (miniMapRef.current) { miniMapRef.current.remove(); miniMapRef.current = null; pinMarkerRef.current = null }
      return
    }
    const timer = setTimeout(() => {
      if (!miniMapContainerRef.current || miniMapRef.current) return
      const lat = form.latitude ? parseFloat(form.latitude) : DEFAULT_CENTER[0]
      const lng = form.longitude ? parseFloat(form.longitude) : DEFAULT_CENTER[1]
      const map = L.map(miniMapContainerRef.current, { center: [lat, lng], zoom: form.latitude ? 14 : 10, zoomControl: true })
      L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', { maxZoom: 20, attribution: '' }).addTo(map)
      if (form.latitude && form.longitude) {
        const marker = L.marker([lat, lng], { icon: buildMarkerIcon(form.facilityType, true), draggable: true }).addTo(map)
        marker.on('dragend', () => {
          const pos = marker.getLatLng()
          setForm(prev => ({ ...prev, latitude: String(pos.lat), longitude: String(pos.lng) }))
          setAddrResolved(true)
        })
        pinMarkerRef.current = marker
      }
      map.on('click', (e) => {
        if (!relocatingPin && addrResolved) return
        const { lat: cLat, lng: cLng } = e.latlng
        setForm(prev => ({ ...prev, latitude: String(cLat), longitude: String(cLng) }))
        setAddrResolved(true)
        if (pinMarkerRef.current) { pinMarkerRef.current.setLatLng([cLat, cLng]) }
        else {
          const marker = L.marker([cLat, cLng], { icon: buildMarkerIcon('regional_landfill', true), draggable: true }).addTo(map)
          marker.on('dragend', () => {
            const pos = marker.getLatLng()
            setForm(prev => ({ ...prev, latitude: String(pos.lat), longitude: String(pos.lng) }))
            setAddrResolved(true)
          })
          pinMarkerRef.current = marker
        }
        setRelocatingPin(false)
      })
      miniMapRef.current = map
    }, 150)
    return () => clearTimeout(timer)
  }, [open]) // eslint-disable-line

  useEffect(() => {
    const map = miniMapRef.current
    if (!map || !form.latitude || !form.longitude) return
    const lat = parseFloat(form.latitude); const lng = parseFloat(form.longitude)
    if (isNaN(lat) || isNaN(lng)) return
    map.setView([lat, lng], 14)
    if (pinMarkerRef.current) pinMarkerRef.current.setLatLng([lat, lng])
    else {
      const marker = L.marker([lat, lng], { icon: buildMarkerIcon(form.facilityType, true), draggable: true }).addTo(map)
      marker.on('dragend', () => {
        const pos = marker.getLatLng()
        setForm(prev => ({ ...prev, latitude: String(pos.lat), longitude: String(pos.lng) }))
        setAddrResolved(true)
      })
      pinMarkerRef.current = marker
    }
  }, [form.latitude, form.longitude]) // eslint-disable-line

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }))

  const handleNameInput = (e) => {
    const val = e.target.value
    setForm(prev => ({ ...prev, name: val }))
    if (nameTimer.current) clearTimeout(nameTimer.current)
    if (val.trim().length < 2) { setNameResults([]); setShowNameResults(false); return }
    nameTimer.current = setTimeout(async () => {
      try {
        const resp = await axios.get(`${url}/api/bid-estimation/disposal-facilities/name-lookup`, { params: { q: val.trim() } })
        const results = resp.data?.results || []
        setNameResults(results); setShowNameResults(results.length > 0)
      } catch (err) { console.error('Name lookup:', err) }
    }, 300)
  }

  const selectNameResult = (r) => {
    const addr = [r.address, r.city, r.state, r.zip].filter(Boolean).join(', ')
    setForm(prev => ({
      ...prev, name: r.name || prev.name, address: addr || prev.address,
      phone: r.phone || prev.phone,
      latitude: r.lat != null ? String(r.lat) : prev.latitude,
      longitude: r.lng != null ? String(r.lng) : prev.longitude
    }))
    setNameResults([]); setShowNameResults(false)
    if (r.lat != null) setAddrResolved(true)
  }

  const handleAddrInput = (e) => {
    const val = e.target.value
    setForm(prev => ({ ...prev, address: val }))
    setAddrResolved(false)
    if (addrTimer.current) clearTimeout(addrTimer.current)
    if (val.trim().length < 3) { setAddrSuggestions([]); setShowAddrSuggestions(false); return }
    addrTimer.current = setTimeout(async () => {
      try {
        const resp = await axios.get(`${url}/api/bid-estimation/disposal-facilities/address-search`, { params: { q: val.trim() } })
        const results = resp.data?.suggestions || []
        if (results.length > 0) { setAddrSuggestions(results); setShowAddrSuggestions(true) }
      } catch (err) { console.error('Addr search:', err) }
    }, 400)
  }

  const selectAddrSuggestion = (s) => {
    setForm(prev => ({ ...prev, address: s.formatted, latitude: String(s.lat), longitude: String(s.lng) }))
    setAddrSuggestions([]); setShowAddrSuggestions(false); setAddrResolved(true)
  }

  const toggleDay = (day) => setStructuredHours(prev => ({ ...prev, [day]: { ...prev[day], open: !prev[day]?.open } }))
  const setDayTime = (day, field, val) => setStructuredHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: val } }))
  const applyToAllOpen = (day) => {
    const src = structuredHours[day]
    if (!src) return
    setStructuredHours(prev => {
      const next = { ...prev }
      DAYS_OF_WEEK.forEach(d => { if (next[d]?.open) next[d] = { ...next[d], openTime: src.openTime, closeTime: src.closeTime } })
      return next
    })
  }

  const toggleMaterial = (cat) => setForm(prev => ({
    ...prev,
    acceptedMaterials: prev.acceptedMaterials.includes(cat)
      ? prev.acceptedMaterials.filter(c => c !== cat)
      : [...prev.acceptedMaterials, cat]
  }))

  const setMatDetail = (cat, field, val) => setForm(prev => ({
    ...prev, materialDetails: { ...prev.materialDetails, [cat]: { ...(prev.materialDetails[cat] || {}), [field]: val } }
  }))

  const handleSubmit = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const hours = structuredToHoursString(structuredHours)
      const payload = {
        name: form.name.trim(), address: form.address.trim(), owner: form.owner.trim(),
        facilityType: form.facilityType, hours, phone: form.phone.trim(),
        payment: form.payment.trim(), notes: form.notes.trim(),
        defaultDumpFee: form.defaultDumpFee ? Number(form.defaultDumpFee) : undefined,
        dumpFeeUnit: form.dumpFeeUnit,
        doesNotAccept: form.doesNotAccept ? form.doesNotAccept.split(',').map(s => s.trim()).filter(Boolean) : [],
        acceptedMaterials: form.acceptedMaterials.map(cat => ({
          category: cat,
          details: form.materialDetails[cat]?.details || MATERIAL_LABELS[cat] || cat,
          costPerTon: form.materialDetails[cat]?.costPerTon ? Number(form.materialDetails[cat].costPerTon) : undefined,
          costPerLoad: form.materialDetails[cat]?.costPerLoad ? Number(form.materialDetails[cat].costPerLoad) : undefined
        }))
      }
      if (form.latitude && form.longitude) {
        payload.location = { type: 'Point', coordinates: [parseFloat(form.longitude), parseFloat(form.latitude)] }
      }
      if (isEdit) await axios.put(`${url}/api/bid-estimation/disposal-facilities/${editItem._id}`, payload)
      else await axios.post(`${url}/api/bid-estimation/disposal-facilities`, payload)
      onSave(); onClose()
    } catch (err) { console.error('Save facility error:', err) }
    finally { setSaving(false) }
  }

  const hasCoords = !!(form.latitude && form.longitude)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
    >
      {/* Header */}
      <div style={{
        background: 'var(--df-primary, #1f5e3a)',
        padding: '18px 24px', color: '#fbf8f1'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)'
          }}>
            <LocationOnIcon style={{ fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.3 }}>
              {isEdit ? editItem?.name : 'New Disposal Site'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 1 }}>
              {isEdit ? 'Edit facility details, location, hours and pricing' : 'Add a new disposal facility to the database'}
            </div>
          </div>
        </div>
      </div>

      <DialogContent style={{ padding: 0 }}>
        <div style={{ display: 'flex', minHeight: 500 }}>
          {/* LEFT — Form */}
          <div style={{ flex: '1 1 55%', padding: '20px 24px', overflowY: 'auto', maxHeight: 600 }}>

            <SectionHeader>Facility Info</SectionHeader>

            {/* Name with lookup */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <TextField label="Site Name" value={form.name || ''} onChange={handleNameInput}
                fullWidth variant="outlined" size="small" autoFocus={!isEdit}
                InputProps={{ style: { fontSize: 13 } }}
              />
              {showNameResults && nameResults.length > 0 && (
                <ClickAwayListener onClickAway={() => setShowNameResults(false)}>
                  <Paper elevation={8} style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300,
                    maxHeight: 200, overflowY: 'auto', marginTop: 2, borderRadius: 8
                  }}>
                    {nameResults.map((r, i) => (
                      <div key={i} onClick={() => selectNameResult(r)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--line, #e0d8c2)', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2, #f6f1e3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        {r.type === 'vendor' ? <BusinessIcon style={{ color: 'var(--ink-3, #6f7a6b)', fontSize: 18 }} /> : <HomeWorkIcon style={{ color: 'var(--ink-3, #6f7a6b)', fontSize: 18 }} />}
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--ink, #1c2419)' }}>{r.name}</div>
                          {r.address && <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>{[r.address, r.city, r.state].filter(Boolean).join(', ')}</div>}
                        </div>
                      </div>
                    ))}
                  </Paper>
                </ClickAwayListener>
              )}
            </div>

            {/* Address with autocomplete */}
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <TextField label="Address" value={form.address || ''} onChange={handleAddrInput}
                fullWidth variant="outlined" size="small" InputProps={{ style: { fontSize: 13 } }}
              />
              {showAddrSuggestions && addrSuggestions.length > 0 && (
                <ClickAwayListener onClickAway={() => setShowAddrSuggestions(false)}>
                  <Paper elevation={8} style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1300,
                    maxHeight: 200, overflowY: 'auto', marginTop: 2, borderRadius: 8
                  }}>
                    {addrSuggestions.map((s, i) => (
                      <div key={i} onClick={() => selectAddrSuggestion(s)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13, transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2, #f6f1e3)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <LocationOnIcon style={{ color: 'var(--ink-3, #6f7a6b)', fontSize: 18 }} />
                        <span>{s.formatted}</span>
                      </div>
                    ))}
                  </Paper>
                </ClickAwayListener>
              )}
            </div>

            <Grid container spacing={1} style={{ marginBottom: 12 }}>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Facility Type</InputLabel>
                  <Select value={form.facilityType || 'regional_landfill'} onChange={handleChange('facilityType')} label="Facility Type"
                    style={{ fontSize: 13 }}>
                    {Object.entries(FACILITY_TYPE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Owner" value={form.owner || ''} onChange={handleChange('owner')}
                  fullWidth variant="outlined" size="small" InputProps={{ style: { fontSize: 13 } }} />
              </Grid>
            </Grid>

            <Grid container spacing={1} style={{ marginBottom: 12 }}>
              <Grid item xs={6}>
                <TextField label="Phone" value={form.phone || ''} onChange={handleChange('phone')} fullWidth variant="outlined" size="small" InputProps={{ style: { fontSize: 13 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Payment" value={form.payment || ''} onChange={handleChange('payment')} fullWidth variant="outlined" size="small" placeholder="e.g. Debit/credit only" InputProps={{ style: { fontSize: 13 } }} />
              </Grid>
            </Grid>

            <SectionHeader style={{ marginTop: 20 }}>Dump Fee</SectionHeader>
            <Grid container spacing={1} style={{ marginBottom: 12 }}>
              <Grid item xs={4}>
                <TextField label="Amount ($)" type="number" value={form.defaultDumpFee || ''} onChange={handleChange('defaultDumpFee')}
                  fullWidth variant="outlined" size="small" inputProps={{ min: 0, step: 0.01 }} InputProps={{ style: { fontSize: 13 } }} />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Unit</InputLabel>
                  <Select value={form.dumpFeeUnit || 'PER_LOAD'} onChange={handleChange('dumpFeeUnit')} label="Unit" style={{ fontSize: 13 }}>
                    <MenuItem value="PER_TON">Per Ton</MenuItem>
                    <MenuItem value="PER_LOAD">Per Load</MenuItem>
                    <MenuItem value="FLAT">Flat</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4} />
            </Grid>

            <SectionHeader style={{ marginTop: 20 }}>Notes</SectionHeader>
            <TextField value={form.notes || ''} onChange={handleChange('notes')}
              fullWidth variant="outlined" size="small" multiline rows={2} placeholder="Operational notes, tips, warnings..."
              InputProps={{ style: { fontSize: 13 } }} style={{ marginBottom: 12 }} />
            <TextField label="Does NOT accept" value={form.doesNotAccept || ''} onChange={handleChange('doesNotAccept')}
              fullWidth variant="outlined" size="small" placeholder="hazardous waste, asbestos, medical waste"
              InputProps={{ style: { fontSize: 13 } }} />
          </div>

          {/* RIGHT — Map + Hours */}
          <div style={{ flex: '1 1 45%', background: 'var(--surface-2, #f6f1e3)', borderLeft: '1px solid var(--line, #e0d8c2)', display: 'flex', flexDirection: 'column' }}>
            {/* Map */}
            <div style={{ padding: '16px 16px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <SectionHeader style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Location</SectionHeader>
                {hasCoords && !relocatingPin && (
                  <Button size="small" onClick={() => setRelocatingPin(true)}
                    style={{ fontSize: 10, textTransform: 'none', color: 'var(--ink-3, #6f7a6b)', minWidth: 0, padding: '2px 8px' }}>
                    Relocate pin
                  </Button>
                )}
                {relocatingPin && (
                  <span style={{ fontSize: 10, color: 'var(--accent, #c66a45)', fontWeight: 600 }}>Click map to place pin</span>
                )}
              </div>
              <div ref={miniMapContainerRef} style={{
                width: '100%', height: 220, borderRadius: 8, overflow: 'hidden',
                border: '1px solid var(--line, #e0d8c2)', background: 'var(--bg-2, #ece5d3)'
              }} />
              {/* Coordinate display */}
              <div style={{ marginTop: 6, minHeight: 20 }}>
                {hasCoords ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                      background: 'var(--primary, #1f5e3a)', flexShrink: 0
                    }} />
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-2, #3d463a)', letterSpacing: 0.3 }}>
                      {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <WarningIcon style={{ fontSize: 13, color: 'var(--accent, #c66a45)' }} />
                    <span style={{ fontSize: 11, color: 'var(--accent, #c66a45)' }}>No coordinates — click map or search address</span>
                  </div>
                )}
              </div>
            </div>

            {/* Hours — compact grid */}
            <div style={{ padding: '12px 16px', flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <SectionHeader style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Hours</SectionHeader>
              </div>
              <div style={{
                background: 'var(--surface, #fbf8f1)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)',
                overflow: 'hidden'
              }}>
                {DAYS_OF_WEEK.map((day, idx) => {
                  const d = structuredHours[day] || { open: false, openTime: '07:00', closeTime: '16:00' }
                  return (
                    <div key={day} style={{
                      display: 'flex', alignItems: 'center', padding: '4px 10px',
                      borderBottom: idx < 6 ? '1px solid var(--line, #e0d8c2)' : 'none',
                      background: d.open ? 'var(--surface, #fbf8f1)' : 'var(--surface-2, #f6f1e3)'
                    }}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={d.open} onChange={() => toggleDay(day)}
                          style={{ padding: 3, color: d.open ? 'var(--primary, #1f5e3a)' : 'var(--ink-4, #97a094)' }} />}
                        label={<span style={{ fontSize: 12, fontWeight: 600, color: d.open ? 'var(--ink-2, #3d463a)' : 'var(--ink-4, #97a094)', width: 28, display: 'inline-block' }}>{day}</span>}
                        style={{ marginRight: 4, minWidth: 65 }}
                      />
                      {d.open ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                          <input type="time" value={d.openTime} onChange={e => setDayTime(day, 'openTime', e.target.value)}
                            style={{ border: '1px solid var(--line, #e0d8c2)', borderRadius: 4, padding: '2px 4px', fontSize: 11, width: 90, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                          <span style={{ color: 'var(--ink-4, #97a094)', fontSize: 10 }}>\u2013</span>
                          <input type="time" value={d.closeTime} onChange={e => setDayTime(day, 'closeTime', e.target.value)}
                            style={{ border: '1px solid var(--line, #e0d8c2)', borderRadius: 4, padding: '2px 4px', fontSize: 11, width: 90, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                          <span onClick={() => applyToAllOpen(day)}
                            style={{ fontSize: 9, color: 'var(--ink-4, #97a094)', cursor: 'pointer', marginLeft: 'auto', whiteSpace: 'nowrap' }}
                            title="Apply to all open days"
                          >apply all</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', fontStyle: 'italic' }}>Closed</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom — Materials */}
        <div style={{ borderTop: '1px solid var(--line, #e0d8c2)', padding: '16px 24px 20px' }}>
          <SectionHeader>
            Accepted Materials
            <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--ink-4, #97a094)', marginLeft: 8 }}>
              {(form.acceptedMaterials || []).length} selected — click to toggle
            </span>
          </SectionHeader>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: (form.acceptedMaterials || []).length > 0 ? 14 : 0 }}>
            {MATERIAL_CATEGORIES.map(cat => {
              const selected = (form.acceptedMaterials || []).includes(cat)
              return (
                <Chip key={cat} size="small" label={MATERIAL_LABELS[cat]}
                  onClick={() => toggleMaterial(cat)}
                  style={{
                    backgroundColor: selected ? (MATERIAL_COLORS[cat]?.color || '#666') : 'transparent',
                    color: selected ? '#fff' : 'var(--ink-3, #6f7a6b)',
                    border: selected ? 'none' : '1px solid var(--line, #e0d8c2)',
                    fontWeight: selected ? 600 : 400,
                    cursor: 'pointer', fontSize: 11, transition: 'all 0.15s'
                  }}
                />
              )
            })}
          </div>

          {(form.acceptedMaterials || []).length > 0 && (
            <div style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 80px 80px', gap: 0, fontSize: 11 }}>
                <div style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--ink-3, #6f7a6b)', borderBottom: '1px solid var(--line, #e0d8c2)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Material</div>
                <div style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--ink-3, #6f7a6b)', borderBottom: '1px solid var(--line, #e0d8c2)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Details / Notes</div>
                <div style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--ink-3, #6f7a6b)', borderBottom: '1px solid var(--line, #e0d8c2)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>$/Ton</div>
                <div style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--ink-3, #6f7a6b)', borderBottom: '1px solid var(--line, #e0d8c2)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>$/Load</div>
                {(form.acceptedMaterials || []).map((cat, idx) => {
                  const md = form.materialDetails?.[cat] || {}
                  const isLast = idx === form.acceptedMaterials.length - 1
                  const borderStyle = isLast ? {} : { borderBottom: '1px solid var(--line, #e0d8c2)' }
                  return (
                    <React.Fragment key={cat}>
                      <div style={{ padding: '5px 10px', display: 'flex', alignItems: 'center', ...borderStyle }}>
                        <Chip size="small" label={MATERIAL_LABELS[cat]}
                          style={matChipStyle(cat, { fontSize: 9, height: 20 })} />
                      </div>
                      <div style={{ padding: '3px 10px', ...borderStyle }}>
                        <input value={md.details || ''} onChange={e => setMatDetail(cat, 'details', e.target.value)}
                          placeholder={MATERIAL_LABELS[cat]}
                          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, outline: 'none', padding: '2px 0', fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ padding: '3px 10px', ...borderStyle }}>
                        <input type="number" value={md.costPerTon || ''} onChange={e => setMatDetail(cat, 'costPerTon', e.target.value)}
                          placeholder="—" min="0" step="0.01"
                          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, outline: 'none', padding: '2px 0', fontFamily: 'inherit' }} />
                      </div>
                      <div style={{ padding: '3px 10px', ...borderStyle }}>
                        <input type="number" value={md.costPerLoad || ''} onChange={e => setMatDetail(cat, 'costPerLoad', e.target.value)}
                          placeholder="—" min="0" step="0.01"
                          style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, outline: 'none', padding: '2px 0', fontFamily: 'inherit' }} />
                      </div>
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 24px',
        borderTop: '1px solid var(--line, #e0d8c2)', background: 'var(--surface-2, #f6f1e3)'
      }}>
        <Button onClick={onClose} disabled={saving} style={{ color: 'var(--ink-3, #6f7a6b)', textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!form.name?.trim() || saving}
          startIcon={saving ? <CircularProgress size={14} style={{ color: '#fbf8f1' }} /> : null}
          style={{
            background: saving ? 'var(--ink-4, #97a094)' : 'var(--primary, #1f5e3a)', color: '#fbf8f1', textTransform: 'none',
            borderRadius: 10, padding: '6px 24px', fontWeight: 600, letterSpacing: 0.3
          }}
        >
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Site'}
        </Button>
      </div>
    </Dialog>
  )
}

// ── Read-only Expand Panel ──────────────────────────────────────────

const FacilityDetailsPanel = ({ item }) => (
  <Box py={2} px={1}>
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Facility Details</Typography>
        <Table size="small">
          <TableBody>
            {[
              ['Address', item.address],
              ['Phone', item.phone],
              ['Owner', item.owner],
              ['Payment', item.payment],
              ['Dump Fee', item.defaultDumpFee ? `$${item.defaultDumpFee} ${DUMP_FEE_UNIT_LABELS[item.dumpFeeUnit] || ''}` : null],
              ['Coordinates', item.location?.coordinates ? `${item.location.coordinates[1].toFixed(5)}, ${item.location.coordinates[0].toFixed(5)}` : null]
            ].filter(([, v]) => v).map(([label, value]) => (
              <TableRow key={label}>
                <TableCell style={{ fontWeight: 600, width: 120, fontSize: 12, padding: '4px 8px' }}>{label}</TableCell>
                <TableCell style={{ fontSize: 12, padding: '4px 8px', ...(label === 'Coordinates' ? { fontFamily: 'monospace', fontSize: 11 } : {}) }}>{value}</TableCell>
              </TableRow>
            ))}
            {item.notes && (
              <TableRow>
                <TableCell style={{ fontWeight: 600, width: 120, fontSize: 12, padding: '4px 8px' }}>Notes</TableCell>
                <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>{item.notes}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Accepted Materials ({(item.acceptedMaterials || []).length})
        </Typography>
        {(item.acceptedMaterials || []).length === 0 ? (
          <Typography variant="body2" color="textSecondary">No materials listed.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>Material</TableCell>
                <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>Details</TableCell>
                <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>$/Ton</TableCell>
                <TableCell style={{ fontSize: 11, padding: '4px 8px' }}>$/Load</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.acceptedMaterials.map((m, i) => (
                <TableRow key={i}>
                  <TableCell style={{ padding: '4px 8px' }}>
                    <Chip size="small" label={MATERIAL_LABELS[m.category] || m.category}
                      style={matChipStyle(m.category, { fontSize: 10 })} />
                  </TableCell>
                  <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>{m.details || '—'}</TableCell>
                  <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>{m.costPerTon ? `$${m.costPerTon}` : '—'}</TableCell>
                  <TableCell style={{ fontSize: 12, padding: '4px 8px' }}>{m.costPerLoad ? `$${m.costPerLoad}` : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {item.doesNotAccept?.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" style={{ marginBottom: 4, color: 'var(--disposal, #a9341d)', fontSize: 12 }}>Does NOT Accept</Typography>
            <Typography variant="body2" style={{ fontSize: 12 }}>{item.doesNotAccept.join(', ')}</Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  </Box>
)

// ── Main Component ──────────────────────────────────────────────────

const DisposalFacilitiesTable = ({ url }) => {
  const [facilities, setFacilities] = useState([])
  const [loading, setLoading] = useState(false)
  const [typeFilter, setTypeFilter] = useState('All')
  const [materialFilter, setMaterialFilter] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [seeding, setSeeding] = useState(false)
  const [seedResult, setSeedResult] = useState(null)
  const [showMap, setShowMap] = useState(false)
  const [selectedFacilityId, setSelectedFacilityId] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalItem, setModalItem] = useState(null)

  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])

  // Wire global popup edit callback
  useEffect(() => {
    window.__disposalEditFacility = (id) => {
      const item = facilities.find(f => f._id === id)
      if (item) { setModalItem(item); setModalOpen(true) }
    }
    return () => { window.__disposalEditFacility = null }
  }, [facilities]) // eslint-disable-line

  useEffect(() => { fetchFacilities() }, []) // eslint-disable-line

  const fetchFacilities = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/disposal-facilities`).then(res => {
      setFacilities(res.data || []); setLoading(false)
    }).catch(err => { console.error(err); setLoading(false) })
  }

  const filteredFacilities = React.useMemo(() => {
    let result = facilities
    if (typeFilter !== 'All') {
      if (typeFilter === 'private') result = result.filter(f => f.facilityType === 'private_landfill' || f.facilityType === 'private_transfer_station')
      else result = result.filter(f => f.facilityType === typeFilter)
    }
    if (materialFilter !== 'All') result = result.filter(f => f.acceptedMaterials?.some(m => m.category === materialFilter))
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(f => (f.name || '').toLowerCase().includes(term) || (f.owner || '').toLowerCase().includes(term) || (f.notes || '').toLowerCase().includes(term) || (f.address || '').toLowerCase().includes(term))
    }
    return result
  }, [facilities, typeFilter, materialFilter, searchTerm])

  const handleSeed = () => {
    setSeeding(true); setSeedResult(null)
    axios.post(`${url}/api/bid-estimation/disposal-facilities/seed`).then(res => { setSeedResult(res.data); setSeeding(false); fetchFacilities() })
      .catch(err => { setSeedResult({ error: err.response?.data?.error || err.message }); setSeeding(false) })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/disposal-facilities/${deleteTarget._id}`).then(() => { setDeleteTarget(null); fetchFacilities() }).catch(console.error)
  }

  const handleRowClick = useCallback((item) => {
    setSelectedFacilityId(item._id)
    if (showMap && mapRef.current && item.location?.coordinates?.length === 2) {
      const [lon, lat] = item.location.coordinates
      mapRef.current.setView([lat, lon], 14)
      const marker = markersRef.current.find(m => m._facilityId === item._id)
      if (marker) marker.openPopup()
    }
  }, [showMap])

  useEffect(() => {
    if (!showMap) return
    if (!mapContainerRef.current || mapRef.current) return
    const map = L.map(mapContainerRef.current, { center: DEFAULT_CENTER, zoom: 10, zoomControl: true })
    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', { maxZoom: 20, attribution: '' }).addTo(map)
    mapRef.current = map
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null } }
  }, [showMap])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    markersRef.current.forEach(m => map.removeLayer(m)); markersRef.current = []
    const valid = filteredFacilities.filter(i => i.location?.coordinates?.length === 2 && i.location.coordinates[0] !== 0)
    const bounds = []
    valid.forEach(item => {
      const [lon, lat] = item.location.coordinates
      const marker = L.marker([lat, lon], { icon: buildMarkerIcon(item.facilityType) })
      marker._facilityId = item._id
      const dist = yardDistLabel(item)
      const mats = (item.acceptedMaterials || []).map(m =>
        `<span style="display:inline-block;background:${(MATERIAL_COLORS[m.category] || {}).bg || '#f0f0f0'};color:${(MATERIAL_COLORS[m.category] || {}).color || '#666'};font-size:9px;padding:1px 5px;border-radius:8px;margin:1px;font-weight:600;border:1px solid ${(MATERIAL_COLORS[m.category] || {}).color || '#666'}22">${MATERIAL_LABELS[m.category] || m.category}</span>`
      ).join('')
      marker.bindPopup(
        `<div style="min-width:200px;font-family:'Geist',-apple-system,sans-serif">` +
        `<div style="font-weight:700;font-size:13px;margin-bottom:4px;color:#1c2419">${item.name}</div>` +
        `<div style="font-size:11px;color:#6f7a6b">${FACILITY_TYPE_LABELS[item.facilityType] || item.facilityType}` +
        (dist ? `<span style="margin-left:6px;color:#97a094">\u00b7 ${dist} from yard</span>` : '') + `</div>` +
        (item.hours ? `<div style="font-size:11px;color:#6f7a6b;margin-top:2px">${item.hours}</div>` : '') +
        (item.phone ? `<div style="font-size:11px;color:#6f7a6b;margin-top:2px">${item.phone}</div>` : '') +
        (mats ? `<div style="margin-top:6px;line-height:1.6">${mats}</div>` : '') +
        `<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e0d8c2">` +
        `<span onclick="window.__disposalEditFacility('${item._id}')" style="font-size:11px;color:#1f5e3a;cursor:pointer;font-weight:600">Edit Site</span>` +
        `</div></div>`
      )
      marker.on('click', () => { setSelectedFacilityId(item._id); setExpandedId(item._id) })
      marker.addTo(map); markersRef.current.push(marker); bounds.push([lat, lon])
    })
    if (bounds.length > 0) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 })
    else map.setView(DEFAULT_CENTER, 10)
  }, [filteredFacilities, showMap])

  useEffect(() => { if (!showMap && mapRef.current) { mapRef.current.remove(); mapRef.current = null } }, [showMap])

  if (loading && facilities.length === 0) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
  }

  return (
    <div>
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Facility Type</InputLabel>
            <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} label="Facility Type">
              {FACILITY_TYPE_FILTER_GROUPS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Material</InputLabel>
            <Select value={materialFilter} onChange={e => setMaterialFilter(e.target.value)} label="Material">
              <MenuItem value="All">All Materials</MenuItem>
              {MATERIAL_CATEGORIES.map(cat => <MenuItem key={cat} value={cat}>{MATERIAL_LABELS[cat]}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <TextField size="small" variant="outlined" placeholder="Search facilities..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon style={{ marginRight: 8, color: 'var(--ink-4, #97a094)' }} fontSize="small" /> }}
          />
        </Grid>
        <Grid item style={{ flex: 1 }} />
        <Grid item>
          <Tooltip title={showMap ? 'List only' : 'Show map'}>
            <Button variant="outlined" size="small" startIcon={showMap ? <ListIcon /> : <MapIcon />}
              onClick={() => setShowMap(!showMap)} style={{ marginRight: 8 }}>
              {showMap ? 'List Only' : 'Map'}
            </Button>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={seeding ? <CircularProgress size={14} /> : <AddIcon />}
            onClick={handleSeed} disabled={seeding || facilities.length > 0}
            style={{ marginRight: 8, borderColor: 'var(--line-2, #cec3a5)', color: 'var(--ink-2, #3d463a)', borderRadius: 10, textTransform: 'none', fontWeight: 600 }}>
            Seed Default Sites
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => { setModalItem(null); setModalOpen(true) }}
            style={{ background: 'var(--primary, #1f5e3a)', color: '#fbf8f1', borderRadius: 10, textTransform: 'none', fontWeight: 600 }}>
            Add Site
          </Button>
        </Grid>
      </Grid>

      {seedResult && (
        <Box mb={2} p={1.5} style={{
          backgroundColor: seedResult.error ? 'var(--disposal-soft, #f7dccf)' : 'var(--primary-soft, #e3eee0)',
          borderRadius: 'var(--r, 10px)', border: `1px solid ${seedResult.error ? 'rgba(169,52,29,.2)' : 'rgba(31,94,58,.2)'}`
        }}>
          <Typography variant="body2" style={{ color: seedResult.error ? 'var(--disposal, #a9341d)' : 'var(--primary, #1f5e3a)' }}>
            {seedResult.error || seedResult.message}
          </Typography>
        </Box>
      )}

      {showMap && (
        <Box mb={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {[['Regional Landfill', '#e53935'], ['County Transfer', '#1e88e5'], ['Inert Landfill', '#78909c'],
            ['City', '#43a047'], ['Private', '#fb8c00'], ['Tribal', '#8e24aa']
          ].map(([label, color]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#555' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />{label}
            </span>
          ))}
        </Box>
      )}

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: showMap ? '0 0 60%' : '1 1 auto', overflow: 'auto', maxHeight: showMap ? 680 : undefined }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 30 }} />
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Accepted Materials</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>From Yard</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFacilities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" style={{ padding: 20 }}>
                      {facilities.length === 0 ? 'No disposal facilities found. Use "Seed Default Sites" to populate Maricopa County facilities.' : 'No facilities match your filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredFacilities.map(item => {
                const isExpanded = expandedId === item._id
                const isSelected = selectedFacilityId === item._id
                return (
                  <React.Fragment key={item._id}>
                    <TableRow hover style={{
                      cursor: 'pointer', opacity: item.isActive === false ? 0.5 : 1,
                      backgroundColor: isSelected && showMap ? 'var(--primary-soft, #e3eee0)' : undefined
                    }} onClick={() => { setExpandedId(isExpanded ? null : item._id); handleRowClick(item) }}>
                      <TableCell style={{ width: 30, padding: '4px 8px' }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" style={{ color: 'var(--ink-4, #97a094)' }} /> : <ExpandMoreIcon fontSize="small" style={{ color: 'var(--ink-4, #97a094)' }} />}
                      </TableCell>
                      <TableCell>
                        <span>
                          {item.name}
                          {!item.location?.coordinates && <WarningIcon style={{ fontSize: 12, color: 'var(--equipment, #b35d1a)', marginLeft: 4, verticalAlign: 'middle' }} titleAccess="No coordinates" />}
                          {item.owner && <Typography variant="caption" color="textSecondary" style={{ display: 'block', fontSize: 10 }}>{item.owner}</Typography>}
                        </span>
                      </TableCell>
                      <TableCell><Chip size="small" label={FACILITY_TYPE_LABELS[item.facilityType] || item.facilityType} variant="outlined" /></TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {(item.acceptedMaterials || []).map((m, i) => (
                            <Chip key={i} size="small" label={MATERIAL_LABELS[m.category] || m.category}
                              style={matChipStyle(m.category, { fontSize: 10, height: 20 })} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell><Typography variant="caption">{item.hours || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{yardDistLabel(item) || '—'}</Typography></TableCell>
                      <TableCell align="right" onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" onClick={() => { setModalItem(item); setModalOpen(true) }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => setDeleteTarget(item)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} style={{ paddingBottom: 0, paddingTop: 0 }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit><FacilityDetailsPanel item={item} /></Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {showMap && (
          <div style={{ flex: '1 1 40%', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(205,216,209,0.9)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div ref={mapContainerRef} style={{ width: '100%', height: 680, backgroundColor: '#e8efe4' }} />
          </div>
        )}
      </div>

      <FacilityFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={fetchFacilities} editItem={modalItem} url={url} />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Deactivate Disposal Site</DialogTitle>
        <DialogContent>
          <Typography>Deactivate "{deleteTarget?.name}"? This will hide it from active queries but preserve the record.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Deactivate</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default DisposalFacilitiesTable
